import { useState, useEffect, useMemo, useRef } from "react";
import { Icon } from "../../components/icon";
import RecipientsTable from "../../components/recipients-table";
import VoiceMessageSummary from "../../components/VoiceMessageSummary";
import TextMessageSummary from "../../components/TextMessageSummary";
import EmailSummary from "../../components/EmailSummary";
import AudioPlayer from "../../components/audio-player";
import CircularProgress from "../../actions/CircularProgress";
import actions from "../../actions/actions";
import { AlertType, MAX_DISPLAY } from "../../utilities/constants";
import "../../styles/analytic.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatScheduledDate(scheduledDate) {
  if (!scheduledDate) return "";
  const d = new Date(scheduledDate);
  if (!isNaN(d.getTime())) {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit", 
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return scheduledDate;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function DetailItem({ label, value }) {
  return (
    <div className="message-details-item" aria-label={label}>
      <div className="md-label">{label}</div>
      <div className="md-value">{value}</div>
    </div>
  );
}

function ExpandablePillList({
  items,
  renderPill,
  getItemTitle,
  showAll,
  onToggle,
}) {
  const visible = showAll ? items : items.slice(0, MAX_DISPLAY);
  const extras = showAll ? [] : items.slice(MAX_DISPLAY);
  const extraTitle = extras.map(getItemTitle).filter(Boolean).join(", ");

  const handleKey = (e, action) => {
    if (e.key === "Enter" || e.key === " ") action();
  };

  return (
    <div className="md-pill-list">
      {visible.map(renderPill)}

      {extras.length > 0 && (
        <span
          className="md-pill"
          title={extraTitle}
          role="button"
          tabIndex={0}
          onClick={() => onToggle(true)}
          onKeyDown={(e) => handleKey(e, () => onToggle(true))}
        >
          +{extras.length} more
        </span>
      )}

      {showAll && items.length > MAX_DISPLAY && (
        <span
          className="md-pill"
          role="button"
          tabIndex={0}
          onClick={() => onToggle(false)}
          onKeyDown={(e) => handleKey(e, () => onToggle(false))}
        >
          Show less
        </span>
      )}
    </div>
  );
}

// ─── PS-specific analytics actions passed to RecipientsTable ─────────────────

const psAnalyticsActions = {
  getMessagePreviewVoiceRecipientList: (req) =>
    actions.parishSoftApis.getMessagePreviewVoiceRecipientList(req),
  getMessagePreviewSmsRecipientList: (req) =>
    actions.parishSoftApis.getMessagePreviewSmsRecipientList(req),
  getMessagePreviewEmailRecipientList: (req) =>
    actions.parishSoftApis.getMessagePreviewEmailRecipientList(req),
};

// ─── Page component ───────────────────────────────────────────────────────────

export default function PSAnalytic({ alert }) {
  const scheduledDisplay = useMemo(
    () => formatScheduledDate(alert?.scheduledDate),
    [alert?.scheduledDate],
  );

  const groups = useMemo(() => alert?.groups ?? [], [alert]);

  const { regularGroups, tempGroups } = useMemo(() => {
    const regular = [];
    const temp = [];
    for (const g of groups) {
      if (g?.is_temp === 1) temp.push(g);
      else regular.push(g);
    }
    return { regularGroups: regular, tempGroups: temp };
  }, [groups]);

  const [groupMembers, setGroupMembers] = useState([]);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);

  const [audioUrls, setAudioUrls] = useState(null);
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    if (alert?.alertType !== AlertType.Voice) return;
    if (alert?.voiceFileIdentifier && alert.voiceFileIdentifier !== "Pending") {
      setAudioUrls(alert?.voiceFileUrl);
    } else if (alert?.message) {
      actions.parishSoftApis.textToSpeech(alert.message).then((response) => {
        if (response?.success && isMountedRef.current) {
          setAudioUrls(response?.fileUrl);
        }
      });
    }
  }, [alert?.alertType, alert?.voiceFileIdentifier, alert?.voiceFileUrl, alert?.message]);

  const [voiceSummary, setVoiceSummary] = useState(null);
  const [smsSummary, setSmsSummary] = useState(null);
  const [emailSummary, setEmailSummary] = useState(null);

  const [voiceTabStatus, setVoiceTabStatus] = useState(0);
  const [smsTabStatus, setSmsTabStatus] = useState(0);
  const [emailTabStatus, setEmailTabStatus] = useState(0);

  const tempGroupId = tempGroups[0]?.id;
  useEffect(() => {
    if (!tempGroupId) {
      setGroupMembers([]);
      return;
    }

    let cancelled = false;
    actions.groups
      .getAllGroupMembers(tempGroupId)
      .then((details) => {
        if (!cancelled) setGroupMembers(details?.members ?? []);
      })
      .catch((err) => console.error("Error fetching group details:", err));

    return () => {
      cancelled = true;
    };
  }, [tempGroupId]);

  return (
    <div className="analytics-page" role="tabpanel" aria-label="Analytics">
      {/* ── Message Details ─────────────────────────────────────────────── */}
      <section className="message-details" aria-label="Message Details">
        <h2 className="message-details-title">Message Details</h2>

        <div className="message-details-grid">
          <DetailItem label="Sent By" value={alert?.senderName} />
          <DetailItem label="Sent On" value={scheduledDisplay || "-"} />
          <DetailItem label="Notification" value={alert?.notification} />
          <DetailItem label="Segmentation" value={alert?.segmentation} />
          <DetailItem
            label="Priority"
            value={alert?.isEmergency ? "Emergency" : "Non-Emergency"}
          />
        </div>

        {regularGroups.length > 0 && (
          <div className="message-details-groups">
            <div className="md-label">Groups</div>
            <ExpandablePillList
              items={regularGroups}
              showAll={showAllGroups}
              onToggle={setShowAllGroups}
              getItemTitle={(g) => g?.name ?? ""}
              renderPill={(group, index) => (
                <span
                  key={group?.id ?? group?.groupId ?? index}
                  className="md-pill"
                >
                  <Icon icon="users" className="fas" />{" "}
                  <span>{group?.name}</span>
                </span>
              )}
            />
          </div>
        )}

        {groupMembers.length > 0 && (
          <div className="message-details-groups">
            <div className="md-label">Individual</div>
            <ExpandablePillList
              items={groupMembers}
              showAll={showAllMembers}
              onToggle={setShowAllMembers}
              getItemTitle={(m) =>
                [m?.firstName, m?.lastName].filter(Boolean).join(" ")
              }
              renderPill={(member, index) => (
                <span key={member.userId ?? index} className="md-pill">
                  <Icon icon="user" className="fas" />{" "}
                  <span>
                    {member.firstName} {member.lastName}
                  </span>
                </span>
              )}
            />
          </div>
        )}
      </section>

      {/* ── Channel Summary ──────────────────────────────────────────────── */}
      <section
        className="voicemsg-smry-section"
        aria-label="Voice Message Summary"
      >
        {alert?.alertType === AlertType.Voice && (
          <VoiceMessageSummary
            summary={voiceSummary}
            onFilterChange={setVoiceTabStatus}
          />
        )}
        {alert?.alertType === AlertType.Text && (
          <TextMessageSummary
            summary={smsSummary}
            onFilterChange={setSmsTabStatus}
          />
        )}
        {alert?.alertType === AlertType.Email && (
          <EmailSummary
            summary={emailSummary}
            onFilterChange={setEmailTabStatus}
          />
        )}
      </section>

      {/* ── Recipients ──────────────────────────────────────────────────── */}
      <section className="recipients-section" aria-label="Recipients">
        <h2 className="recipients-title">Recipients</h2>
        <RecipientsTable
          alert={alert}
          analyticsActions={psAnalyticsActions}
          hideSegmentActions={true}
          onSummaryLoaded={setVoiceSummary}
          voiceTabStatus={voiceTabStatus}
          onVoiceTabChange={setVoiceTabStatus}
          smsTabStatus={smsTabStatus}
          onSmsTabChange={setSmsTabStatus}
          onSmsSummaryLoaded={setSmsSummary}
          emailTabStatus={emailTabStatus}
          onEmailTabChange={setEmailTabStatus}
          onEmailSummaryLoaded={setEmailSummary}
        />
      </section>

      {/* ── Message Content ─────────────────────────────────────────────── */}
      <section className="message-content-section" aria-label="Message Content">
        <div className="message-content-inner">
          <h2 className="message-content-title">Message Content</h2>
          <div className="message-content-field">
            <span className="message-content-label">Message</span>
            <div className="message-content-body">
              {alert?.alertType === AlertType.Voice ? (
                <div className="mc-audio-player" role="region" aria-label="Voice message player">
                  {audioUrls ? (
                    <AudioPlayer src={audioUrls} autoPlay={false} />
                  ) : (
                    <CircularProgress />
                  )}
                </div>
              ) : (
                <p className="message-content-text">{alert?.message || ""}</p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
